// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Verisum — in-browser checksum & hash verifier.
 * Bootstraps the UI, owns the file → stream → hash → verify lifecycle, and
 * keeps heavy hashing in a dedicated worker fed by File.stream().
 */

import './styles/main.css';
import type {
  AlgoId,
  HashSet,
  Tab,
  TextInputMode,
  WorkerResponse,
} from './types';
import {
  ALGORITHMS,
  ALGO_IDS,
  algoInfo,
  algoLabel,
  DEFAULT_SELECTED,
  DEFAULT_VISIBLE,
  preferredAlgoForLength,
} from './algorithms';
import { hashAll, hmacHex, HMAC_ALGOS } from './hasher';
import { decodeInput } from './encoding';
import { normalizeExpected, verifyExpected } from './verify';
import { escapeHtml, formatBytes, formatDuration, formatThroughput, pct } from './format';
import { categoryLogger, emit, mountEventDrawer } from './eventlog';
import { closeModal, initModals, isModalOpen, openModal, toast } from './ui';
import { initGlossary } from './glossary';

const PREFS_KEY = 'verisum.prefs.v1';

interface Prefs {
  algos: AlgoId[];
  tab: Tab;
  showAllAlgos: boolean;
  textMode: TextInputMode;
  hmacAlgo: AlgoId;
}

const log = {
  system: categoryLogger('system'),
  read: categoryLogger('read'),
  hash: categoryLogger('hash'),
  verify: categoryLogger('verify'),
  output: categoryLogger('output'),
};

/* ------------------------------------------------------------------ state */

interface FileEntry {
  id: string;
  file: File;
  status: 'queued' | 'hashing' | 'done' | 'error';
  hashes: HashSet;
  processed: number;
  total: number;
  bytesPerSec: number;
  elapsedMs: number;
  error?: string;
  jobId: number;
  row?: HTMLElement;
}

let prefs: Prefs = loadPrefs();
const selectedAlgos = new Set<AlgoId>(prefs.algos);
let showAllAlgos = prefs.showAllAlgos;
let activeTab: Tab = prefs.tab;
let expected = '';

const order: string[] = [];
const entries = new Map<string, FileEntry>();
let idSeq = 0;

let worker: Worker | null = null;
let jobSeq = 0;
let curJobId = 0;
const queue: string[] = [];

let textToken = 0;
let hmacToken = 0;

/* ------------------------------------------------------------------ prefs */

function loadPrefs(): Prefs {
  const base: Prefs = {
    algos: [...DEFAULT_SELECTED],
    tab: 'files',
    showAllAlgos: false,
    textMode: 'utf8',
    hmacAlgo: 'sha256',
  };
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Prefs>;
      const algos = Array.isArray(p.algos)
        ? p.algos.filter((a): a is AlgoId => (ALGO_IDS as string[]).includes(a))
        : base.algos;
      return {
        algos: algos.length ? algos : base.algos,
        tab: p.tab === 'text' || p.tab === 'hmac' ? p.tab : 'files',
        showAllAlgos: !!p.showAllAlgos,
        textMode: p.textMode === 'hex' || p.textMode === 'base64' ? p.textMode : 'utf8',
        hmacAlgo: HMAC_ALGOS.includes(p.hmacAlgo as AlgoId) ? (p.hmacAlgo as AlgoId) : 'sha256',
      };
    }
  } catch {
    /* ignore */
  }
  return base;
}

function savePrefs(): void {
  prefs = {
    algos: [...selectedAlgos],
    tab: activeTab,
    showAllAlgos,
    textMode: (document.getElementById('text-mode') as HTMLSelectElement | null)?.value as TextInputMode ?? prefs.textMode,
    hmacAlgo: (document.getElementById('hmac-algo') as HTMLSelectElement | null)?.value as AlgoId ?? prefs.hmacAlgo,
  };
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

/** Selected algorithms in canonical registry order. */
function orderedSelected(): AlgoId[] {
  return ALGO_IDS.filter((a) => selectedAlgos.has(a));
}

/* ----------------------------------------------------------------- render */

function render(): void {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <header class="topbar">
      <a class="brand" href="/">
        <svg class="brand-mark" viewBox="0 0 32 32" aria-hidden="true">
          <rect width="32" height="32" rx="7" fill="#0e1014"/>
          <g stroke="#243138" stroke-width="2" stroke-linecap="round">
            <line x1="9" y1="7" x2="7.5" y2="25"/>
            <line x1="16" y1="7" x2="14.5" y2="25"/>
            <line x1="6" y1="13" x2="22" y2="13"/>
            <line x1="5" y1="19" x2="21" y2="19"/>
          </g>
          <path d="M15 20.5 L20 25.5 L28 13.5" fill="none" stroke="#2fe6c4" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="brand-name">veri<span class="accent">sum</span></span>
      </a>
      <nav class="topnav">
        <button type="button" data-modal="how">How it works</button>
        <button type="button" data-modal="threat">Privacy</button>
        <button type="button" data-modal="about">About</button>
        <button type="button" id="toggle-drawer" class="drawer-toggle" aria-pressed="false">Event log</button>
      </nav>
    </header>

    <button type="button" class="trust-banner" data-modal="threat" title="What is and isn't protected">
      <span class="lock">&#128274;</span> Runs entirely in your browser. Your files are never uploaded.
    </button>

    <main class="main-content">
      <div class="workspace">
        <div class="tabs" role="tablist">
          <button type="button" class="tab" role="tab" data-tab="files">Files</button>
          <button type="button" class="tab" role="tab" data-tab="text">Text</button>
          <button type="button" class="tab" role="tab" data-tab="hmac">HMAC</button>
        </div>

        <!-- ============================ FILES ============================ -->
        <section class="panel-tab" id="tab-files">
          <section class="dropzone" id="dropzone" tabindex="0" role="button"
            aria-label="Choose files to hash">
            <svg viewBox="0 0 64 64" class="dz-icon" aria-hidden="true">
              <rect x="12" y="8" width="40" height="48" rx="5" fill="none" stroke="currentColor" stroke-width="3"/>
              <line x1="20" y1="22" x2="44" y2="22" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
              <line x1="20" y1="30" x2="44" y2="30" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
              <line x1="20" y1="38" x2="34" y2="38" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
            </svg>
            <p class="dz-title">Drop a file to checksum</p>
            <p class="dz-sub">or <span class="dz-link">choose files</span> — hash several at once</p>
            <p class="dz-formats">Any file, any size. Streamed and hashed on your device — nothing is uploaded.</p>
            <button type="button" class="sample-link" id="sample-btn">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
              Try a sample file
            </button>
            <input type="file" id="file-input" multiple hidden />
          </section>

          <div class="algo-bar">
            <span class="algo-bar-label">Algorithms</span>
            <div class="algo-chips" id="algo-chips"></div>
            <button type="button" class="algo-more" id="algo-more"></button>
          </div>

          <div class="verify-bar" id="verify-bar">
            <label for="verify-input" class="verify-label">Verify against a published checksum</label>
            <div class="verify-input-wrap">
              <input type="text" id="verify-input" class="verify-input" spellcheck="false"
                autocomplete="off" autocapitalize="off"
                placeholder="Paste an expected hash, e.g. e3b0c442…  (or a full SHA256SUMS line)" />
              <button type="button" class="verify-clear" id="verify-clear" aria-label="Clear" hidden>&times;</button>
            </div>
            <div class="verify-status" id="verify-status" data-state="idle"></div>
          </div>

          <div class="file-list" id="file-list"></div>
          <div class="empty-hint" id="empty-hint">Drop a file above to compute its checksums.</div>
        </section>

        <!-- ============================= TEXT ============================= -->
        <section class="panel-tab" id="tab-text" hidden>
          <div class="text-head">
            <label for="text-mode" class="io-label">Interpret input as</label>
            <select id="text-mode" class="io-select">
              <option value="utf8">Text (UTF-8)</option>
              <option value="hex">Hex</option>
              <option value="base64">Base64</option>
            </select>
            <span class="byte-count" id="text-bytes">0 bytes</span>
          </div>
          <textarea id="text-input" class="text-input" spellcheck="false"
            placeholder="Type or paste text to hash — every algorithm updates live. Nothing leaves this tab."></textarea>
          <div class="hash-table" id="text-hashes"></div>
        </section>

        <!-- ============================= HMAC ============================= -->
        <section class="panel-tab" id="tab-hmac" hidden>
          <p class="hmac-intro">A keyed hash — mixes a secret key into the digest so only holders of the key can reproduce it. Used to sign webhooks and API requests.</p>
          <div class="hmac-grid">
            <div class="hmac-field">
              <label for="hmac-key" class="io-label">Secret key</label>
              <div class="hmac-row">
                <input type="text" id="hmac-key" class="io-input" spellcheck="false" autocomplete="off" placeholder="your-secret-key" />
                <select id="hmac-key-mode" class="io-select sm">
                  <option value="utf8">UTF-8</option>
                  <option value="hex">Hex</option>
                  <option value="base64">Base64</option>
                </select>
              </div>
            </div>
            <div class="hmac-field">
              <label for="hmac-algo" class="io-label">Algorithm</label>
              <select id="hmac-algo" class="io-select"></select>
            </div>
          </div>
          <label for="hmac-msg" class="io-label">Message</label>
          <textarea id="hmac-msg" class="text-input" spellcheck="false"
            placeholder="The message to authenticate."></textarea>
          <div class="hmac-out" id="hmac-out"></div>
        </section>
      </div>
    </main>

    <aside class="drawer" id="drawer" hidden><div id="drawer-mount"></div></aside>

    <footer class="site-footer">
      Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      · <a href="https://sites.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
    </footer>
  `;

  app.querySelectorAll('[data-modal]').forEach((el) =>
    el.addEventListener('click', () => openModal((el as HTMLElement).dataset.modal!)),
  );
  app.querySelectorAll<HTMLElement>('.tab').forEach((el) =>
    el.addEventListener('click', () => switchTab(el.dataset.tab as Tab)),
  );

  wireDropzone();
  wireVerify();
  wireText();
  wireHmac();
  renderAlgoChips();
  document.getElementById('algo-more')!.addEventListener('click', () => {
    showAllAlgos = !showAllAlgos;
    savePrefs();
    renderAlgoChips();
  });
  document.getElementById('toggle-drawer')!.addEventListener('click', toggleDrawer);

  applyTab();
}

/* ------------------------------------------------------------------- tabs */

function switchTab(tab: Tab): void {
  activeTab = tab;
  savePrefs();
  applyTab();
}

function applyTab(): void {
  for (const t of ['files', 'text', 'hmac'] as Tab[]) {
    document.getElementById(`tab-${t}`)!.hidden = t !== activeTab;
    document.querySelector(`.tab[data-tab="${t}"]`)?.classList.toggle('on', t === activeTab);
  }
}

/* -------------------------------------------------------------- algo chips */

function renderAlgoChips(): void {
  const wrap = document.getElementById('algo-chips')!;
  wrap.innerHTML = '';
  const visible = showAllAlgos ? ALGORITHMS : ALGORITHMS.filter((a) => DEFAULT_VISIBLE.includes(a.id));
  for (const a of visible) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'algo-chip';
    chip.dataset.algo = a.id;
    chip.classList.toggle('on', selectedAlgos.has(a.id));
    if (!a.strong) chip.classList.add('weak');
    chip.title = a.note;
    chip.setAttribute('aria-pressed', String(selectedAlgos.has(a.id)));
    chip.innerHTML = `<span class="chk">✓</span>${escapeHtml(a.label)}`;
    chip.addEventListener('click', () => toggleAlgo(a.id));
    wrap.appendChild(chip);
  }
  const more = document.getElementById('algo-more')!;
  more.textContent = showAllAlgos ? 'Fewer algorithms' : 'More algorithms';
}

function toggleAlgo(id: AlgoId): void {
  if (selectedAlgos.has(id)) {
    if (selectedAlgos.size === 1) {
      toast('Keep at least one algorithm selected.', 'info');
      return;
    }
    selectedAlgos.delete(id);
    log.hash(`Disabled ${algoLabel(id)}`);
  } else {
    selectedAlgos.add(id);
    log.hash(`Enabled ${algoLabel(id)}`);
  }
  savePrefs();
  renderAlgoChips();
  requeueAll();
  updateVerify();
}

/* --------------------------------------------------------------- dropzone */

function wireDropzone(): void {
  const dz = document.getElementById('dropzone')!;
  const input = document.getElementById('file-input') as HTMLInputElement;

  dz.addEventListener('click', () => input.click());
  dz.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      input.click();
    }
  });
  input.addEventListener('change', () => {
    if (input.files) addFiles(input.files);
    input.value = '';
  });

  const stop = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
  };
  ['dragenter', 'dragover'].forEach((ev) =>
    dz.addEventListener(ev, (e) => {
      stop(e);
      dz.classList.add('drag');
    }),
  );
  ['dragleave', 'dragend'].forEach((ev) =>
    dz.addEventListener(ev, (e) => {
      stop(e);
      dz.classList.remove('drag');
    }),
  );
  dz.addEventListener('drop', (e) => {
    stop(e);
    dz.classList.remove('drag');
    const files = (e as DragEvent).dataTransfer?.files;
    if (files && files.length) addFiles(files);
  });

  // "Try a sample" pill lives inside the dropzone, so stop the click from
  // bubbling up and opening the file picker.
  document.getElementById('sample-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    void loadSample();
  });
}

/**
 * Fetch the bundled sample file and route it through the exact same ingestion
 * path a real upload uses (`addFiles`), so it gets streamed, hashed and shown
 * identically.
 */
async function loadSample(): Promise<void> {
  try {
    log.read('Loading sample file');
    const res = await fetch('samples/demo.txt');
    if (!res.ok) throw new Error(`sample fetch ${res.status}`);
    const blob = await res.blob();
    const file = new File([blob], 'verisum-sample.txt', { type: 'text/plain' });
    addFiles([file]);
  } catch (err) {
    toast('Could not load the sample file.', 'err');
    log.read(`Sample failed: ${err instanceof Error ? err.message : String(err)}`, 'err');
  }
}

function addFiles(list: FileList | File[]): void {
  ensureWorker();
  let added = 0;
  for (const file of Array.from(list)) {
    const id = `f${++idSeq}`;
    const entry: FileEntry = {
      id,
      file,
      status: 'queued',
      hashes: {},
      processed: 0,
      total: file.size,
      bytesPerSec: 0,
      elapsedMs: 0,
      jobId: 0,
    };
    entries.set(id, entry);
    order.push(id);
    createRow(entry);
    queue.push(id);
    added++;
    log.read(`Queued “${file.name}” (${formatBytes(file.size)})`);
  }
  if (added) {
    document.getElementById('empty-hint')!.hidden = true;
    switchTab('files');
    pump();
  }
}

function removeEntry(id: string): void {
  const entry = entries.get(id);
  if (!entry) return;
  if (entry.jobId === curJobId && curJobId !== 0) {
    worker?.postMessage({ type: 'abort', jobId: curJobId });
    curJobId = 0;
  }
  entries.delete(id);
  const idx = order.indexOf(id);
  if (idx !== -1) order.splice(idx, 1);
  const qi = queue.indexOf(id);
  if (qi !== -1) queue.splice(qi, 1);
  entry.row?.remove();
  document.getElementById('empty-hint')!.hidden = order.length > 0;
  pump();
  updateVerify();
}

/* --------------------------------------------------------- worker / queue */

function ensureWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('./hash-worker.ts', import.meta.url), { type: 'module' });
    worker.addEventListener('message', onWorkerMessage);
    worker.addEventListener('error', (e) => {
      log.hash(`Worker error: ${e.message}`, 'err');
    });
  }
  return worker;
}

function pump(): void {
  if (curJobId !== 0) return;
  const id = queue.shift();
  if (id == null) return;
  const entry = entries.get(id);
  if (!entry) {
    pump();
    return;
  }
  const algos = orderedSelected();
  entry.status = 'hashing';
  entry.hashes = {};
  entry.processed = 0;
  entry.total = entry.file.size;
  curJobId = ++jobSeq;
  entry.jobId = curJobId;
  updateRow(entry);
  log.hash(`Hashing “${entry.file.name}” with ${algos.map(algoLabel).join(', ')}`);
  ensureWorker().postMessage({ type: 'hash', jobId: curJobId, file: entry.file, algos });
}

function onWorkerMessage(e: MessageEvent<WorkerResponse>): void {
  const m = e.data;
  if (m.jobId !== curJobId) return; // stale (superseded by a re-hash or removal)
  const entry = [...entries.values()].find((x) => x.jobId === m.jobId);
  if (!entry) {
    if (m.type !== 'progress') {
      curJobId = 0;
      pump();
    }
    return;
  }
  if (m.type === 'progress') {
    entry.processed = m.processed;
    entry.bytesPerSec = m.bytesPerSec;
    updateRowProgress(entry);
  } else if (m.type === 'done') {
    entry.status = 'done';
    entry.hashes = m.hashes;
    entry.elapsedMs = m.elapsedMs;
    entry.processed = entry.total;
    curJobId = 0;
    updateRow(entry);
    log.hash(
      `Done “${entry.file.name}” in ${formatDuration(m.elapsedMs)}` +
        (entry.total > 0 && m.elapsedMs > 0
          ? ` (${formatThroughput((entry.total / m.elapsedMs) * 1000)})`
          : ''),
      'ok',
    );
    updateVerify();
    pump();
  } else if (m.type === 'error') {
    entry.status = 'error';
    entry.error = m.message;
    curJobId = 0;
    updateRow(entry);
    log.hash(`Failed “${entry.file.name}”: ${m.message}`, 'err');
    pump();
  }
}

/** Re-hash every file with the current algorithm set. */
function requeueAll(): void {
  if (curJobId !== 0) {
    worker?.postMessage({ type: 'abort', jobId: curJobId });
    curJobId = 0;
  }
  queue.length = 0;
  for (const id of order) {
    const entry = entries.get(id);
    if (!entry) continue;
    entry.status = 'queued';
    entry.hashes = {};
    entry.processed = 0;
    updateRow(entry);
    queue.push(id);
  }
  pump();
}

/* ----------------------------------------------------------------- rows */

function createRow(entry: FileEntry): void {
  const list = document.getElementById('file-list')!;
  const row = document.createElement('div');
  row.className = 'file-row';
  row.dataset.id = entry.id;
  entry.row = row;
  list.appendChild(row);
  updateRow(entry);
}

function updateRow(entry: FileEntry): void {
  const row = entry.row;
  if (!row) return;
  const algos = orderedSelected();

  const statusBadge =
    entry.status === 'done'
      ? '<span class="fr-badge ok">hashed</span>'
      : entry.status === 'error'
        ? '<span class="fr-badge err">error</span>'
        : entry.status === 'hashing'
          ? '<span class="fr-badge run">hashing…</span>'
          : '<span class="fr-badge">queued</span>';

  const hashesHtml =
    entry.status === 'done'
      ? algos.map((a) => hashLineHtml(a, entry.hashes[a] ?? '')).join('')
      : entry.status === 'error'
        ? `<div class="fr-error">${escapeHtml(entry.error ?? 'Hashing failed.')}</div>`
        : `<div class="fr-progress">
             <div class="pbar"><span style="width:${pct(entry.processed, entry.total)}%"></span></div>
             <span class="pmeta" data-role="pmeta">${pct(entry.processed, entry.total)}% · ${formatBytes(entry.processed)} / ${formatBytes(entry.total)}</span>
           </div>`;

  row.innerHTML = `
    <div class="fr-head">
      <div class="fr-name" title="${escapeHtml(entry.file.name)}">${escapeHtml(entry.file.name)}</div>
      <div class="fr-meta">${formatBytes(entry.total)} ${statusBadge}</div>
      <button type="button" class="fr-remove" aria-label="Remove file">&times;</button>
    </div>
    <div class="fr-body">${hashesHtml}</div>
  `;

  row.querySelector('.fr-remove')?.addEventListener('click', () => removeEntry(entry.id));
  row.querySelectorAll<HTMLElement>('.copy-btn').forEach((btn) =>
    btn.addEventListener('click', () => {
      const value = btn.dataset.copy ?? '';
      void copyText(value, btn);
    }),
  );
  row.querySelectorAll<HTMLElement>('.copy-line').forEach((btn) =>
    btn.addEventListener('click', () => {
      const algo = btn.dataset.algo as AlgoId;
      const line = `${entry.hashes[algo]}  ${entry.file.name}`;
      void copyText(line, btn);
    }),
  );

  applyRowVerify(entry);
}

function updateRowProgress(entry: FileEntry): void {
  const row = entry.row;
  if (!row) return;
  const fill = row.querySelector<HTMLElement>('.pbar span');
  const meta = row.querySelector<HTMLElement>('[data-role="pmeta"]');
  const p = pct(entry.processed, entry.total);
  if (fill) fill.style.width = `${p}%`;
  if (meta) {
    meta.textContent =
      `${p}% · ${formatBytes(entry.processed)} / ${formatBytes(entry.total)}` +
      (entry.bytesPerSec > 0 ? ` · ${formatThroughput(entry.bytesPerSec)}` : '');
  }
}

function hashLineHtml(algo: AlgoId, value: string): string {
  const info = algoInfo(algo);
  return `
    <div class="hash-line" data-algo="${algo}">
      <span class="hl-algo${info.strong ? '' : ' weak'}">${escapeHtml(info.label)}</span>
      <code class="hl-value" data-algo="${algo}">${escapeHtml(value)}</code>
      <span class="hl-actions">
        <button type="button" class="copy-btn" data-copy="${escapeHtml(value)}" title="Copy digest">Copy</button>
        <button type="button" class="copy-line" data-algo="${algo}" title="Copy “hash  filename” line">Line</button>
      </span>
    </div>`;
}

/* --------------------------------------------------------------- verify */

function wireVerify(): void {
  const input = document.getElementById('verify-input') as HTMLInputElement;
  const clear = document.getElementById('verify-clear')!;
  input.addEventListener('input', () => {
    expected = input.value;
    clear.hidden = expected.trim() === '';
    onExpectedChanged();
  });
  clear.addEventListener('click', () => {
    input.value = '';
    expected = '';
    clear.hidden = true;
    onExpectedChanged();
    input.focus();
  });
}

/** Auto-enable the detected algorithm (if needed) then refresh verify state. */
function onExpectedChanged(): void {
  const norm = normalizeExpected(expected);
  if (norm && /^[0-9a-f]+$/.test(norm)) {
    const pref = preferredAlgoForLength(norm.length);
    if (pref && !selectedAlgos.has(pref)) {
      selectedAlgos.add(pref);
      savePrefs();
      renderAlgoChips();
      log.verify(`Enabled ${algoLabel(pref)} to match the pasted ${norm.length}-char checksum`);
      requeueAll();
    }
  }
  updateVerify();
}

function unionForVerify(): { anyPending: boolean; matchEntry?: FileEntry; matchAlgo?: AlgoId } {
  let anyPending = false;
  for (const id of order) {
    const entry = entries.get(id);
    if (!entry) continue;
    const r = verifyExpected(expected, entry.hashes);
    if (r.status === 'match') return { anyPending: false, matchEntry: entry, matchAlgo: r.matchedAlgo };
    if (entry.status === 'queued' || entry.status === 'hashing') anyPending = true;
    if (r.status === 'pending') anyPending = true;
  }
  return { anyPending };
}

function updateVerify(): void {
  const statusEl = document.getElementById('verify-status');
  if (!statusEl) return;
  const norm = normalizeExpected(expected);

  if (norm === '') {
    statusEl.dataset.state = 'idle';
    statusEl.innerHTML = '';
    for (const id of order) {
      const e = entries.get(id);
      if (e) applyRowVerify(e);
    }
    return;
  }
  if (!/^[0-9a-f]+$/.test(norm)) {
    statusEl.dataset.state = 'unknown';
    statusEl.innerHTML = `<span class="vs-icon">?</span> That doesn't look like a hex checksum.`;
    return;
  }
  const candidates = verifyExpected(expected, {}).candidates;
  if (candidates.length === 0) {
    statusEl.dataset.state = 'unknown';
    statusEl.innerHTML = `<span class="vs-icon">?</span> No known algorithm produces a ${norm.length}-character digest.`;
    return;
  }

  const { anyPending, matchEntry, matchAlgo } = unionForVerify();
  if (matchEntry && matchAlgo) {
    statusEl.dataset.state = 'match';
    const many = order.length > 1;
    statusEl.innerHTML =
      `<span class="vs-icon">✓</span> <strong>MATCH</strong> — ${algoLabel(matchAlgo)}` +
      (many ? ` · <span class="vs-file">${escapeHtml(matchEntry.file.name)}</span>` : '');
    log.verify(`MATCH: ${algoLabel(matchAlgo)} of “${matchEntry.file.name}”`, 'ok');
  } else if (order.length === 0) {
    statusEl.dataset.state = 'pending';
    statusEl.innerHTML = `<span class="vs-icon">…</span> Drop the file to check it against this ${algoLabel(candidates[0])} checksum.`;
  } else if (anyPending) {
    statusEl.dataset.state = 'pending';
    statusEl.innerHTML = `<span class="vs-icon">…</span> Hashing — comparing as soon as it finishes.`;
  } else {
    statusEl.dataset.state = 'mismatch';
    statusEl.innerHTML = `<span class="vs-icon">✗</span> <strong>NO MATCH</strong> — none of the current file(s) produce this ${candidates.map(algoLabel).join(' / ')} digest.`;
    log.verify('NO MATCH against current files', 'warn');
  }
  for (const id of order) {
    const e = entries.get(id);
    if (e) applyRowVerify(e);
  }
}

/** Mark the specific hash line in a row that matches the expected value. */
function applyRowVerify(entry: FileEntry): void {
  const row = entry.row;
  if (!row) return;
  row.querySelectorAll<HTMLElement>('.hash-line').forEach((el) => el.classList.remove('matched'));
  const norm = normalizeExpected(expected);
  if (!norm || !/^[0-9a-f]+$/.test(norm)) return;
  const r = verifyExpected(expected, entry.hashes);
  if (r.status === 'match' && r.matchedAlgo) {
    row.querySelector<HTMLElement>(`.hash-line[data-algo="${r.matchedAlgo}"]`)?.classList.add('matched');
  }
}

/* ------------------------------------------------------------------ text */

function wireText(): void {
  const input = document.getElementById('text-input') as HTMLTextAreaElement;
  const mode = document.getElementById('text-mode') as HTMLSelectElement;
  mode.value = prefs.textMode;
  let timer = 0;
  const schedule = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(computeText, 180);
  };
  input.addEventListener('input', schedule);
  mode.addEventListener('change', () => {
    savePrefs();
    computeText();
  });
  computeText();
}

async function computeText(): Promise<void> {
  const input = document.getElementById('text-input') as HTMLTextAreaElement;
  const mode = (document.getElementById('text-mode') as HTMLSelectElement).value as TextInputMode;
  const out = document.getElementById('text-hashes')!;
  const bytesEl = document.getElementById('text-bytes')!;
  const token = ++textToken;

  let data: Uint8Array;
  try {
    data = decodeInput(input.value, mode);
  } catch (err) {
    bytesEl.textContent = 'invalid input';
    out.innerHTML = `<div class="hash-note err">${escapeHtml(err instanceof Error ? err.message : String(err))}</div>`;
    return;
  }
  bytesEl.textContent = `${data.length.toLocaleString()} byte${data.length === 1 ? '' : 's'}`;

  try {
    const hashes = await hashAll(ALGO_IDS, data);
    if (token !== textToken) return;
    out.innerHTML = ALGO_IDS.map((a) => hashLineHtml(a, hashes[a] ?? '')).join('');
    wireCopyButtons(out, hashes, null);
  } catch (err) {
    if (token !== textToken) return;
    out.innerHTML = `<div class="hash-note err">${escapeHtml(err instanceof Error ? err.message : String(err))}</div>`;
  }
}

/* ------------------------------------------------------------------ hmac */

function wireHmac(): void {
  const algoSel = document.getElementById('hmac-algo') as HTMLSelectElement;
  algoSel.innerHTML = HMAC_ALGOS.map((a) => `<option value="${a}">${algoLabel(a)}</option>`).join('');
  algoSel.value = prefs.hmacAlgo;

  const key = document.getElementById('hmac-key') as HTMLInputElement;
  const keyMode = document.getElementById('hmac-key-mode') as HTMLSelectElement;
  const msg = document.getElementById('hmac-msg') as HTMLTextAreaElement;

  let timer = 0;
  const schedule = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(computeHmac, 180);
  };
  key.addEventListener('input', schedule);
  msg.addEventListener('input', schedule);
  keyMode.addEventListener('change', computeHmac);
  algoSel.addEventListener('change', () => {
    savePrefs();
    computeHmac();
  });
  computeHmac();
}

async function computeHmac(): Promise<void> {
  const key = (document.getElementById('hmac-key') as HTMLInputElement).value;
  const keyMode = (document.getElementById('hmac-key-mode') as HTMLSelectElement).value as TextInputMode;
  const msg = (document.getElementById('hmac-msg') as HTMLTextAreaElement).value;
  const algo = (document.getElementById('hmac-algo') as HTMLSelectElement).value as AlgoId;
  const out = document.getElementById('hmac-out')!;
  const token = ++hmacToken;

  if (key === '' && msg === '') {
    out.innerHTML = `<div class="hash-note">Enter a key and a message to compute an HMAC.</div>`;
    return;
  }

  let keyBytes: Uint8Array;
  try {
    keyBytes = decodeInput(key, keyMode);
  } catch (err) {
    out.innerHTML = `<div class="hash-note err">Key: ${escapeHtml(err instanceof Error ? err.message : String(err))}</div>`;
    return;
  }

  try {
    const digest = await hmacHex(algo, keyBytes, new TextEncoder().encode(msg));
    if (token !== hmacToken) return;
    out.innerHTML = hashLineHtml(algo, digest).replace('hash-line', 'hash-line hmac-line');
    const map: HashSet = { [algo]: digest };
    wireCopyButtons(out, map, null);
  } catch (err) {
    if (token !== hmacToken) return;
    out.innerHTML = `<div class="hash-note err">${escapeHtml(err instanceof Error ? err.message : String(err))}</div>`;
  }
}

/* ------------------------------------------------------------- copy utils */

function wireCopyButtons(container: HTMLElement, hashes: HashSet, filename: string | null): void {
  container.querySelectorAll<HTMLElement>('.copy-btn').forEach((btn) =>
    btn.addEventListener('click', () => void copyText(btn.dataset.copy ?? '', btn)),
  );
  container.querySelectorAll<HTMLElement>('.copy-line').forEach((btn) =>
    btn.addEventListener('click', () => {
      const algo = btn.dataset.algo as AlgoId;
      const line = filename ? `${hashes[algo]}  ${filename}` : (hashes[algo] ?? '');
      void copyText(line, btn);
    }),
  );
}

async function copyText(text: string, btn?: HTMLElement): Promise<void> {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    log.output('Copied to clipboard');
    if (btn) {
      const prev = btn.textContent;
      btn.textContent = 'Copied';
      btn.classList.add('copied');
      window.setTimeout(() => {
        btn.textContent = prev;
        btn.classList.remove('copied');
      }, 1100);
    } else {
      toast('Copied to clipboard', 'ok');
    }
  } catch {
    toast('Could not access the clipboard.', 'err');
  }
}

/* --------------------------------------------------------------- drawer */

let drawerMounted = false;
function toggleDrawer(): void {
  const drawer = document.getElementById('drawer')!;
  if (drawer.hidden) openDrawer();
  else closeDrawer();
}
function openDrawer(): void {
  const drawer = document.getElementById('drawer')!;
  const btn = document.getElementById('toggle-drawer')!;
  drawer.hidden = false;
  btn.setAttribute('aria-pressed', 'true');
  btn.classList.add('on');
  if (!drawerMounted) {
    mountEventDrawer(document.getElementById('drawer-mount')!, closeDrawer);
    drawerMounted = true;
  }
}
function closeDrawer(): void {
  const drawer = document.getElementById('drawer')!;
  const btn = document.getElementById('toggle-drawer')!;
  drawer.hidden = true;
  btn.setAttribute('aria-pressed', 'false');
  btn.classList.remove('on');
}
function isDrawerOpen(): boolean {
  return !document.getElementById('drawer')!.hidden;
}

/* ------------------------------------------------------------ shortcuts */

function initShortcuts(): void {
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (isModalOpen()) closeModal();
      else if (isDrawerOpen()) closeDrawer();
    }
  });
  // Paste a hash into the verify box from anywhere on the Files tab.
  document.addEventListener('paste', (e) => {
    if (activeTab !== 'files') return;
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    const text = e.clipboardData?.getData('text');
    if (text && normalizeExpected(text)) {
      const input = document.getElementById('verify-input') as HTMLInputElement;
      input.value = text.trim();
      expected = input.value;
      (document.getElementById('verify-clear') as HTMLElement).hidden = false;
      onExpectedChanged();
      input.focus();
    }
  });
}

/* ------------------------------------------------------------ bootstrap */

function main(): void {
  render();
  initModals();
  initGlossary();
  initShortcuts();
  emit('system', 'ok', 'Verisum ready — no server, nothing uploaded');
  // Service worker (offline support) is registered by vite-plugin-pwa's
  // auto-injected registration — no manual navigator.serviceWorker.register here.
}

main();
